import { useState, useMemo, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useThunk } from "../../hooks/useThunk";
import { fetchConsentLogs } from "../../services/thunks/consentLogsThunk";
import { exportConsentLogs } from "../../services/thunks/exportCSVThunk";
import Table from "../../components/Table/Table";
import { roles } from "../../constants/commonConstants";
import { Check } from "lucide-react";
import DropDown, { DropdownItem } from "../../components/Inputs/DropDown";
import showToast from "../../utils/toastNotifications";
import dayjs from "dayjs";
import CustomButton from "../../components/Buttons/CustomButton";

// Icons
import exportIcon from "../../assets/icons/export.svg";
import calendarIcon from "../../assets/icons/calendar.svg";

const ConsentLogs = () => {
  const auth = useSelector((state) => state.auth);
  const { role } = auth;
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [highlightedRowId, setHighlightedRowId] = useState(null);

  // Filter states
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const [category, setCategory] = useState("");
  const [source, setSource] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewCurrent, setViewCurrent] = useState(false);

  const tableRef = useRef(null);
  const pageSetterRef = useRef(null);

  const [doFetchData, isFetchingData] = useThunk(exportConsentLogs);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tableRef.current && !tableRef.current.contains(event.target)) {
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleExportCSV = async () => {
    try {
      await doFetchData({
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        category: category || undefined,
        view: viewCurrent ? "current" : "history",
      });
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const handleApplyFilters = () => {
    if (pageSetterRef.current) {
      pageSetterRef.current(1);
    }
    setRefreshTrigger((prev) => prev + 1);
    showToast("info", "Filters applied");
  };

  // Custom filter function for category logic
  const getCategoryFilter = (category) => {
    switch (category) {
      case "analytics":
        return { analytics: true, marketing: false };
      case "marketing":
        return { analytics: false, marketing: true };
      case "all":
        return { analytics: true, marketing: true };
      default:
        return {};
    }
  };

  const handleViewCurrentToggle = () => {
    const next = !viewCurrent;
    setViewCurrent(next);
    if (pageSetterRef.current) {
      pageSetterRef.current(1);
    }
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleClearAll = () => {
    setStartDate("");
    setEndDate("");
    setCategory("");
    setSource("");
    setSearchQuery("");
    setViewCurrent(false);
    if (pageSetterRef.current) {
      pageSetterRef.current(1);
    }
    setRefreshTrigger((prev) => prev + 1);
    showToast("info", "Filters cleared");
  };

  // Define table columns
  const tableColumns = useMemo(() => {
    const columns = [
      {
        title: "User ID",
        dataIndex: "userId",
        key: "userId",
        sorter: true,
        minWidth: "150px",
        render: (text) => (
          <div className="min-h-[32px] text-sm font-[400] font-inter text-[#E2E2E2]">
            {text}
          </div>
        ),
      },
      {
        title: "User Name",
        dataIndex: "userName",
        key: "userName",
        sorter: true,
        minWidth: "200px",
        render: (text) => (
          <div className="min-h-[32px] text-sm font-[400] font-inter text-[#E2E2E2]">
            {text}
          </div>
        ),
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        sorter: true,
        minWidth: "250px",
        render: (text) => (
          <div className="min-h-[32px] text-sm font-[400] font-inter text-[#E2E2E2]">
            {text}
          </div>
        ),
      },
      {
        title: "Analytics",
        dataIndex: "analytics",
        key: "analytics",
        sorter: true,
        minWidth: "100px",
        render: (value) => (
          <div className="flex justify-start">
            {value ? (
              <div className="w-5 h-5 rounded border border-gray-300 flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </div>
            ) : (
              <div className="w-5 h-5 border border-gray-300 rounded"></div>
            )}
          </div>
        ),
      },
      {
        title: "Marketing",
        dataIndex: "marketing",
        key: "marketing",
        sorter: true,
        minWidth: "100px",
        render: (value) => (
          <div className="flex justify-start">
            {value ? (
              <div className="w-5 h-5 rounded border border-gray-300 flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </div>
            ) : (
              <div className="w-5 h-5 border border-gray-300 rounded"></div>
            )}
          </div>
        ),
      },
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        sorter: true,
        minWidth: "120px",
        render: (text) => (
          <div className="text-sm text-[#E2E2E2] font-public-sans flex flex-col">
            <span>{text ? dayjs(text).format("YYYY-MM-DD") : "—"}</span>
            <span>{text ? dayjs(text).format("HH:mm A") : ""}</span>
          </div>
        )
      },
    ];

    return columns;
  }, []);

  return (
    <div className="px-6 py-10 text-white min-h-screen">
      {/* Header */}
      <div className="flex justify-end items-center mb-6">
        <CustomButton
          onClick={handleExportCSV}
          useGradientBorder
          className="w-[149px] h-[45px] text-lg font-semibold font-public-sans py-0 px-0"
          textColor="gradient"
          gradient="bg-[linear-gradient(180deg,#51F8EF_0%,#1A7DD1_100%)]"
        >
          <img src={exportIcon} /> Export CSV
        </CustomButton>
      </div>

      {/* Filters Section */}
      <div className="w-full bg-[#111111d6] border border-[#505050] rounded-[8px] p-6 mb-2">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-[#E3E3E3] font-public-sans font-bold text-[20px] leading-[22px]">
            Filters
          </h2>
          <label className="flex items-center gap-2 cursor-pointer select-none" onClick={handleViewCurrentToggle}>
            <div
              className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                viewCurrent
                  ? "bg-[#1A7DD1] border-[#1A7DD1]"
                  : "bg-transparent border-[#808080]"
              }`}
            >
              {viewCurrent && <Check className="w-3 h-3 text-white" />}
            </div>
            <span className="text-[#E3E3E3] text-[14px] font-public-sans">
              Current
            </span>
          </label>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[280px_1fr_auto] gap-6 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-[#E3E3E3] text-[14px] leading-[22px] font-public-sans">
              Date Range
            </label>
            <div className="flex items-center gap-[10px] bg-[#45444433] border border-[#808080] rounded-[5px] px-3 h-[45px]">
              <div className="relative w-[110px]">
                {/* Label/Display Input */}
                <input
                  type="text"
                  readOnly
                  value={startDate ? dayjs(startDate).format("DD-MM-YYYY") : ""}
                  placeholder="DD/MM/YYYY"
                  className="w-full bg-transparent text-[14px] font-public-sans text-[#B6B6B6CC] pr-4 outline-none cursor-pointer uppercase placeholder:text-[#B6B6B6CC]"
                />
                {/* Invisible Date Picker Overlaid */}
                <input
                  type="date"
                  ref={startDateRef}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
                <img
                  src={calendarIcon}
                  alt="cal"
                  className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none"
                />
              </div>
              <span className="text-[#B6B6B6CC]">-</span>
              <div className="relative w-[110px]">
                {/* Label/Display Input */}
                <input
                  type="text"
                  readOnly
                  value={endDate ? dayjs(endDate).format("DD-MM-YYYY") : ""}
                  placeholder="DD/MM/YYYY"
                  className="w-full bg-transparent text-[14px] font-public-sans text-[#B6B6B6CC] pr-4 outline-none cursor-pointer uppercase placeholder:text-[#B6B6B6CC]"
                />
                {/* Invisible Date Picker Overlaid */}
                <input
                  type="date"
                  ref={endDateRef}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
                <img
                  src={calendarIcon}
                  alt="cal"
                  className="absolute right-0 top-1/2 -translate-y-1/2 w-[14px] h-[14px] pointer-events-none"
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[#E3E3E3] text-[14px] leading-[22px] font-public-sans">
              Category
            </label>
            <div className="w-[250px] h-[45px] bg-[#45444433] border border-[#808080] rounded-[5px] text-[#B6B6B6CC]">
              <DropDown
                selected={category || "category"}
                showItem={category || "category"}
                buttonClassName="h-full bg-transparent border-none"
                textClassName="placeholder:text-[#B6B6B6CC] text-[#B6B6B6CC]"
              >
                <DropdownItem
                  label="All"
                  selected={category === "all"}
                  onClick={() => setCategory("all")}
                  textClassName="text-[#B6B6B6CC]"
                />
                <DropdownItem
                  label="Analytics"
                  selected={category === "analytics"}
                  onClick={() => setCategory("analytics")}
                  textClassName="text-[#B6B6B6CC]"
                />
                <DropdownItem
                  label="Marketing"
                  selected={category === "marketing"}
                  onClick={() => setCategory("marketing")}
                  textClassName="text-[#B6B6B6CC]"
                />
              </DropDown>
            </div>
          </div>
          <div className="flex flex-row gap-3">
            <CustomButton
              useGradientBorder
              className="w-[136px] h-[41px] rounded-[5px] text-[18px] font-medium font-public-sans py-0 px-4"
              rounded="rounded-[5px]"
              textColor="gradient"
              gradient="bg-[linear-gradient(180deg,#51F8EF_0%,#1A7DD1_100%)]"
              onClick={handleApplyFilters}
            >
              Apply Filters
            </CustomButton>
            <CustomButton
              className="w-[100px] h-[45px] rounded-[5px] font-normal py-0 px-0 uppercase"
              rounded="rounded-[5px]"
              textColor="text-[#808989]"
              borderColor="border-[#808989]"
              onClick={handleClearAll}
            >
              Clear All
            </CustomButton>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div ref={tableRef}>
        <Table
          columns={tableColumns}
          fetchData={fetchConsentLogs}
          refreshTrigger={refreshTrigger}
          headingBgColor="#0D141B"
          headingTextColor="#FFFFFF"
          customQueryParams={(params) => {
            const categoryFilter = getCategoryFilter(category);
            return {
              ...params,
              token: auth.token,
              view: viewCurrent ? "current" : "history",
              startDate: startDate || undefined,
              endDate: endDate || undefined,
              analytics: categoryFilter.analytics,
              marketing: categoryFilter.marketing,
              source: source || undefined,
              search: searchQuery.trim() || undefined,
            };
          }}
          highlightedRowId={highlightedRowId}
          setPageCallback={(fn) => (pageSetterRef.current = fn)}
          searchQuery={searchQuery}
        />
      </div>
    </div>
  );
};

export default ConsentLogs;
